import type { IconName } from '@/design-system/icons/Icon';
import type { FeatureId } from './featuresRegistry';

export type RoleId = 'member' | 'visitor' | 'assoc' | 'admin';

export type NavItem = {
  id: string;
  label: string;
  icon: IconName;
  route: string;
  feature?: FeatureId;
  count?: number;
  alert?: boolean;
  associationRoles?: Array<'owner' | 'admin' | 'staff'>;
};

export type NavGroup = {
  label?: string;
  items: NavItem[];
};

export type RoleDef = {
  id: RoleId;
  label: string;
  icon: IconName;
  home: string;
  hasLaudo: boolean;
  identity: { name: string; meta: string; avatar: string; bg: string };
  nav: NavGroup[];
};

export const ROLES: Record<RoleId, RoleDef> = {
  member: {
    id: 'member',
    label: 'Paciente',
    icon: 'user',
    home: '/dashboard',
    hasLaudo: true,
    identity: { name: 'Paciente', meta: 'conta pessoal', avatar: 'PA', bg: 'var(--primary-soft)' },
    nav: [
      { items: [
        { id: 'dashboard', label: 'Início', icon: 'home', route: '/dashboard' },
        { id: 'feed', label: 'Feed', icon: 'activity', route: '/feed', feature: 'feed' },
      ] },
      { label: 'Comunidade', items: [
        { id: 'communities', label: 'Comunidades', icon: 'users', route: '/communities', feature: 'communities', count: 4 },
        { id: 'messages', label: 'Mensagens', icon: 'chat', route: '/messages', feature: 'messages', count: 2 },
        { id: 'notifications', label: 'Notificações', icon: 'inbox', route: '/notifications', feature: 'notifications', count: 5 },
        { id: 'reels', label: 'Reels & lives', icon: 'eye', route: '/reels', feature: 'reels' },
      ] },
      { label: 'Descobrir', items: [
        { id: 'explore', label: 'Explorar', icon: 'grid', route: '/explore', feature: 'explore' },
        { id: 'strains', label: 'Strains', icon: 'leaf', route: '/strains', feature: 'strains' },
        { id: 'effects', label: 'Efeitos', icon: 'activity', route: '/effects' },
        { id: 'associations', label: 'Associações', icon: 'building', route: '/associations', feature: 'associations' },
        { id: 'marketplace', label: 'Marketplace', icon: 'bag', route: '/marketplace', feature: 'marketplace' },
      ] },
      { label: 'Ferramentas', items: [
        { id: 'getLaudo', label: 'Guia de acesso', icon: 'shield', route: '/get-laudo' },
        { id: 'diary', label: 'Diário', icon: 'doc', route: '/diary' },
        { id: 'meds', label: 'Medicamentos', icon: 'pill', route: '/meds' },
        { id: 'recommendations', label: 'Recomendações', icon: 'sparkles', route: '/account/recommendations' },
        { id: 'cohort', label: 'Coorte', icon: 'users', route: '/account/cohort' },
        { id: 'donations', label: 'Fundo solidário', icon: 'heart', route: '/donations', feature: 'donations' },
      ] },
      { label: 'Conta', items: [
        { id: 'report', label: 'Denunciar', icon: 'flag', route: '/report' },
        { id: 'vault', label: 'Documentos', icon: 'lock', route: '/account/documents' },
        { id: 'account', label: 'Minha conta', icon: 'user', route: '/account' },
      ] },
    ],
  },
  visitor: {
    id: 'visitor',
    label: 'Sem laudo',
    icon: 'user',
    home: '/get-laudo',
    hasLaudo: false,
    identity: { name: 'Visitante', meta: 'sem laudo · acesso limitado', avatar: 'VI', bg: 'var(--surface-3)' },
    nav: [
      { items: [
        { id: 'getLaudo', label: 'Como ter acesso', icon: 'shield', route: '/get-laudo' },
        { id: 'dashboard', label: 'Início', icon: 'home', route: '/dashboard' },
      ] },
      { label: 'Comunidade', items: [
        { id: 'feed', label: 'Feed', icon: 'activity', route: '/feed', feature: 'feed' },
        { id: 'communities', label: 'Comunidades', icon: 'users', route: '/communities', feature: 'communities' },
        { id: 'messages', label: 'Mensagens', icon: 'chat', route: '/messages', feature: 'messages' },
        { id: 'reels', label: 'Reels & lives', icon: 'eye', route: '/reels', feature: 'reels' },
      ] },
      { label: 'Descobrir & aprender', items: [
        { id: 'explore', label: 'Explorar', icon: 'grid', route: '/explore', feature: 'explore' },
        { id: 'strains', label: 'Strains', icon: 'leaf', route: '/strains', feature: 'strains' },
        { id: 'effects', label: 'Efeitos', icon: 'activity', route: '/effects' },
        { id: 'associations', label: 'Associações', icon: 'building', route: '/associations', feature: 'associations' },
      ] },
      { label: 'Conta', items: [
        { id: 'report', label: 'Denunciar', icon: 'flag', route: '/report' },
        { id: 'account', label: 'Minha conta', icon: 'user', route: '/account' },
      ] },
    ],
  },
  assoc: {
    id: 'assoc',
    label: 'Associação',
    icon: 'building',
    home: '/panel',
    hasLaudo: false,
    identity: { name: 'Associação', meta: 'conta institucional', avatar: 'AS', bg: 'var(--primary)' },
    nav: [
      { items: [
        { id: 'panel', label: 'Painel', icon: 'home', route: '/panel' },
        { id: 'plan', label: 'Plano', icon: 'star', route: '/panel/plan' },
      ] },
      { label: 'Gestão', items: [
        { id: 'inquiries', label: 'Inquéritos', icon: 'inbox', route: '/messages', feature: 'messages', count: 12 },
        { id: 'publishProduct', label: 'Publicar produto', icon: 'bag', route: '/marketplace/publish', feature: 'marketplace' },
        { id: 'associations', label: 'Diretório público', icon: 'eye', route: '/associations', feature: 'associations' },
        { id: 'reviewsAdmin', label: 'Avaliações', icon: 'star', route: '/manage/reviews', feature: 'associations' },
        { id: 'sales', label: 'Vendas', icon: 'money', route: '/manage/sales', associationRoles: ['owner', 'admin'] },
        { id: 'assocDocs', label: 'Documentos', icon: 'doc', route: '/manage/docs', associationRoles: ['owner', 'admin'] },
        { id: 'team', label: 'Equipe', icon: 'users', route: '/manage/team', associationRoles: ['owner', 'admin'] },
        { id: 'donations', label: 'Fundo solidário', icon: 'heart', route: '/donations', feature: 'donations' },
      ] },
      { label: 'Conteúdo', items: [
        { id: 'reels', label: 'Reels & lives', icon: 'eye', route: '/reels', feature: 'reels' },
        { id: 'communities', label: 'Comunidades', icon: 'users', route: '/communities', feature: 'communities' },
        { id: 'strains', label: 'Strains', icon: 'leaf', route: '/strains', feature: 'strains' },
      ] },
      { label: 'Conta', items: [
        { id: 'report', label: 'Denunciar', icon: 'flag', route: '/report' },
        { id: 'claim', label: 'Verificação', icon: 'shield', route: '/claim', associationRoles: ['owner'] },
        { id: 'account', label: 'Configurações', icon: 'cog', route: '/account' },
      ] },
    ],
  },
  admin: {
    id: 'admin',
    label: 'Admin',
    icon: 'shield',
    home: '/admin',
    hasLaudo: false,
    identity: { name: 'Equipe CanaBiz', meta: 'moderação · backstage', avatar: 'CB', bg: 'var(--ink)' },
    nav: [
      { items: [
        { id: 'admin', label: 'Visão geral', icon: 'home', route: '/admin' },
      ] },
      { label: 'Operação', items: [
        { id: 'complaints', label: 'Denúncias', icon: 'shield', route: '/admin/complaints', count: 3 },
        { id: 'waitlist', label: 'Lista de espera', icon: 'inbox', route: '/admin/waitlist' },
        { id: 'associations', label: 'Associações', icon: 'building', route: '/associations', feature: 'associations' },
        { id: 'communities', label: 'Comunidades', icon: 'users', route: '/communities', feature: 'communities' },
      ] },
      { label: 'Sistema', items: [
        { id: 'features', label: 'Recursos & módulos', icon: 'toggle', route: '/admin/features' },
        { id: 'notifications', label: 'Notificações', icon: 'inbox', route: '/notifications', feature: 'notifications' },
        { id: 'legal', label: 'Legal', icon: 'doc', route: '/admin/legal' },
        { id: 'account', label: 'Configurações', icon: 'cog', route: '/account' },
      ] },
    ],
  },
};

export const ROLE_ORDER: RoleId[] = ['member', 'visitor', 'assoc', 'admin'];
